/*
Copyright 2025 New Vector Ltd.
Copyright 2020 The Matrix.org Foundation C.I.C.
Copyright 2024 Mirian Margiani <mixosaurus+ichthyo@pm.me>

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE files in the repository root for full details.
*/

import {TemplateView, Builder} from "../../general/TemplateView";
import {disableTargetCallback} from "../../general/utils";
import {ViewNode} from "../../general/types";
import {KeyBackupViewModel, Status, BackupWriteStatus} from "../../../../../domain/session/settings/KeyBackupViewModel";
import {KeyType} from "../../../../../matrix/ssss/index";

export class KeyBackupSettingsView extends TemplateView<KeyBackupViewModel> {
    render(t: Builder<KeyBackupViewModel>, vm: KeyBackupViewModel): ViewNode {
        return t.div([
            t.map(vm => vm.status, (status, t, vm) => {
                switch (status) {
                    case Status.Enabled: return renderEnabled(t, vm);
                    case Status.NewVersionAvailable: return renderNewVersionAvailable(t, vm);
                    case Status.SetupWithPassphrase: return renderEnableFromPhrase(t, vm);
                    case Status.SetupWithRecoveryKey: return renderEnableFromKey(t, vm);
                    case Status.Pending: return t.p(vm.i18n`Waiting to go online…`);
                }
            }),
            t.map(vm => vm.backupWriteStatus, (status, t, vm) => {
                switch (status) {
                    case BackupWriteStatus.Writing: {
                        const progress = t.progress({
                            min: 0+"",
                            max: 100+"",
                            value: vm => vm.backupPercentage,
                        });
                        return t.div([`Sicherung läuft `, progress, " ", vm => vm.backupInProgressLabel]);
                    }
                    case BackupWriteStatus.Stopped: {
                        let label;
                        const error = vm.backupError;
                        if (error) {
                            label = `Die Sicherung wurde aufgrund eines Fehlers angehalten: ${vm.backupError}`;
                        } else {
                            label = `Die Sicherung wurde beendet`;
                        }
                        return t.p([label, " ", t.button({onClick: () => vm.startBackup()}, `Backup now`)]);
                    }
                    case BackupWriteStatus.Done:
                        return t.p(`Alle Schlüssel werden gesichert.`);
                    default:
                        return undefined;
                }
            }),
            t.if(vm => vm.isMasterKeyTrusted, t => {
                return t.p("Cross-signing master key found and trusted.")
            }),
            t.if(vm => vm.canSignOwnDevice, t => {
                return t.div([
                    t.button(
                        {
                            onClick: disableTargetCallback(async () => {
                                vm.navigateToVerification();
                            }),
                        },
                        "Verify by emoji"
                    ),
                ]);
            }),

        ]);
    }
}

function renderEnabled(t: Builder<KeyBackupViewModel>, vm: KeyBackupViewModel): ViewNode {
    const items = [
        t.p([vm.i18n`Die Schlüsselsicherung ist aktiviert und verwendet die Sicherungsversion ${vm.backupVersion}. `, t.button({onClick: () => vm.disable()}, vm.i18n`Deaktivieren`)])
    ];
    if (vm.dehydratedDeviceId) {
        items.push(t.p(vm.i18n`Mit der ID ${vm.dehydratedDeviceId} wurde eine dehydrierte Geräte-ID eingerichtet, die Sie bei Ihrer nächsten Anmeldung mit Ihrem geheimen Speicherschlüssel verwenden können.`));
    }
    return t.div(items);
}

function renderNewVersionAvailable(t: Builder<KeyBackupViewModel>, vm: KeyBackupViewModel): ViewNode {
    const items = [
        t.p([vm.i18n`Eine neue Backup-Version wurde von einem anderen Gerät erstellt. Deaktivieren Sie die Schlüsselsicherung und aktivieren Sie sie mit dem neuen Schlüssel erneut.`, t.button({onClick: () => vm.disable()}, vm.i18n`Disable`)])
    ];
    return t.div(items);
}

function renderEnableFromKey(t: Builder<KeyBackupViewModel>, vm: KeyBackupViewModel): ViewNode {
    const useASecurityPhrase = t.button({className: "link", onClick: () => vm.showPhraseSetup()}, vm.i18n`verwende eine Sicherheitsphrase`);
    return t.div([
        t.p(vm.i18n`Geben Sie unten Ihren geheimen Speichersicherheitsschlüssel in ${vm.purpose} ein, der es Ihnen ermöglicht, Nachrichten zu entschlüsseln, die Sie erhalten haben, bevor Sie sich bei dieser Sitzung angemeldet haben. Der Sicherheitsschlüssel ist ein Code aus 12 Gruppen von 4 Zeichen, die durch ein Leerzeichen getrennt sind, das Element für Sie erstellt hat, als Sie die Sicherheit eingerichtet haben.`),
        renderError(t),
        renderEnableFieldRow(t, vm, vm.i18n`Sicherheitsschlüssel`, (key, setupDehydratedDevice) => vm.enterSecurityKey(key, setupDehydratedDevice)),
        t.p([vm.i18n`Alternativ `, useASecurityPhrase, vm.i18n` wenn du eine hast.`]),
    ]);
}

function renderEnableFromPhrase(t: Builder<KeyBackupViewModel>, vm: KeyBackupViewModel): ViewNode {
    const useASecurityKey = t.button({className: "link", onClick: () => vm.showKeySetup()}, vm.i18n`einen Sicherheitsschlüssel verwenden`);
    return t.div([
        t.p(vm.i18n`Gebe deine geheime Speichersicherheitsphrase unten in ${vm.purpose} ein, die es dir ermöglicht, Nachrichten zu entschlüsseln, die Du erhälst haben, bevor Sie sich bei dieser Sitzung angemeldet haben. Die Sicherheitsphrase ist eine frei formulierte geheime Phrase, die Sie optional beim Einrichten der Sicherheit in Element auswählen. Es unterscheidet sich von Deinem Passwort für die Anmeldung, es sei denn, Du hast denselben Wert festgelegt.`),
        renderError(t),
        renderEnableFieldRow(t, vm, vm.i18n`Security phrase`, (phrase, setupDehydratedDevice) => vm.enterSecurityPhrase(phrase, setupDehydratedDevice)),
        t.p([vm.i18n`Du kannst auch `, useASecurityKey, vm.i18n`.`]),
    ]);
}

function renderEnableFieldRow(t, vm, label, callback): ViewNode {
    let setupDehydrationCheck;
    const eventHandler = () => callback(input.value, setupDehydrationCheck?.checked || false);
    const input = t.input({
        type: "password",
        disabled: vm => vm.isBusy,
        placeholder: label,
        autocomplete: "new-password",
        spellcheck: "false",
        autocorrect: "off",
        readonly: true,
        onFocus: () => input.removeAttribute("readonly"),
    });
    const children = [
        t.p([
            input,
            t.button({disabled: vm => vm.isBusy, onClick: eventHandler}, vm.decryptAction),
        ]),
    ];
    if (vm.offerDehydratedDeviceSetup) {
        setupDehydrationCheck = t.input({type: "checkbox", id:"enable-dehydrated-device"});
        const moreInfo = t.a({href: "https://github.com/uhoreg/matrix-doc/blob/dehydration/proposals/2697-device-dehydration.md", target: "_blank", rel: "noopener"}, "more info");
        children.push(t.p([
            setupDehydrationCheck,
            t.label({for: setupDehydrationCheck.id}, [vm.i18n`Gerät auch sichern (`, moreInfo, ")"])
        ]));
    }
    return t.div({className: `row`}, [
        t.div({className: "label"}, label),
        t.div({className: "content"}, children),
    ]);
}

function renderError(t: Builder<KeyBackupViewModel>): ViewNode {
    return t.if(vm => vm.error !== undefined, (t, vm) => {
        return t.div([
            t.p({className: "error"}, vm => vm.i18n`Schlüsselsicherung konnte nicht aktiviert werden: ${vm.error}.`),
            t.p(vm.i18n`Try double checking that you did not mix up your security key, security phrase and login password as explained above.`)
        ])
    });
}

